// S-063-15 / DEC-062: カレンダー取込「生テキスト保管」方式の純関数ユーティリティ（§4.2）。
//
// 貼り付けられた予定表テキストを、会社名/担当者に分解せず「日付ヘッダで日ごとに分割」するだけの
// 正規表現ベースのロジック（Gemini 不使用）。構造化抽出（旧 calendarParser）とは別経路で、
// RC `calendar_rawtext_enabled` が true のときに使う。
//
// やらないこと: Firestore I/O（calendarDaysRepo）、会社名/担当者の構造化抽出、Gemini 呼び出し。
//
// テスト方針（flutter-test-rules を web にも適用）: 純関数のみ。リアル I/O・Date.now 依存は持たず、
//   基準日 `baseDateIso`（JST today・呼び出し側が注入）で相対表現を解決する。

// 1 日分の生テキスト（docID = JST 日付 YYYY-MM-DD）。
export interface RawDay {
  dayId: string; // 'YYYY-MM-DD'
  rawText: string;
}

// S-066-9 / DEC-066 真因(b): 曖昧/欠落した日付見出しの分類。
//   - 'invalid-date'        : 日付の形だが月/日が妥当域外（タイポ等。例 2026-13-01 / 13/40 / 6月40日）
//   - 'day-only'            : 月の無い「N日」見出し（例 20日 / 20日（金）。直前の日と混同しやすい）
//   - 'nonstandard-separator': 日付らしいが受理形式に無い区切り（例 全角スラッシュ 6／20）
export type AmbiguityReason = 'invalid-date' | 'day-only' | 'nonstandard-separator';

export interface AmbiguousHeader {
  reason: AmbiguityReason;
  line: string; // 該当行（trim 済み）。警告表示用。
}

export interface SplitResult {
  days: RawDay[]; // 日付ヘッダで分割できた分（dayId 昇順）
  // 最初の日付ヘッダより前の行＋曖昧見出し以降の隔離行（日付なし。プレビューで日付割当 or 破棄）。
  // S-066-9: 曖昧見出しを検出したら以降を直前の日付バケツに混ぜず、ここへ fail-closed 隔離する。
  undated: string;
  // S-066-9: 検出した曖昧/欠落見出し（可視化・確認導線用。空配列なら曖昧なし）。
  ambiguous: AmbiguousHeader[];
}

// 相対語 → 基準日からの日数オフセット。
const RELATIVE_OFFSETS: Record<string, number> = {
  本日: 0,
  今日: 0,
  明日: 1,
  明後日: 2,
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// 月/日の妥当域（厳密な月末日数までは見ない・過剰拒否より取り込み優先）。
function validMonthDay(mo: number, da: number): boolean {
  return mo >= 1 && mo <= 12 && da >= 1 && da <= 31;
}

// baseDateIso（YYYY-MM-DD）に days を加算した YYYY-MM-DD を返す（UTC 基準で TZ 非依存）。
function addDaysIso(baseDateIso: string, days: number): string {
  const d = new Date(`${baseDateIso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return baseDateIso;
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// 1 行が日付ヘッダなら YYYY-MM-DD を返す。ヘッダでなければ null。
//   認識する形式（行頭・前後の空白許容）:
//     2026-06-19 / 2026/06/19 / 6月19日 / 2026年6月19日 / 6月19日（木） / 6/19 / 06/19(木)
//     相対語 本日 / 今日 / 明日 / 明後日
//   年が無い形式（6/19・6月19日）は baseDate の年で補完する（年跨ぎヒューリスティクスは採らない・プレビュー編集可）。
export function parseDateHeader(line: string, baseDateIso: string): string | null {
  const t = (line || '').trim();
  if (!t) return null;

  // 相対語（行頭一致）。
  for (const word of Object.keys(RELATIVE_OFFSETS)) {
    if (t.startsWith(word)) return addDaysIso(baseDateIso, RELATIVE_OFFSETS[word]);
  }

  // YYYY-MM-DD / YYYY/MM/DD（4 桁年を先に判定）。
  let m = t.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) {
    const mo = Number(m[2]);
    const da = Number(m[3]);
    return validMonthDay(mo, da) ? `${m[1]}-${pad2(mo)}-${pad2(da)}` : null;
  }

  // （YYYY年）?M月D日。
  m = t.match(/^(?:(\d{4})年)?(\d{1,2})月(\d{1,2})日/);
  if (m) {
    const yr = m[1] ? Number(m[1]) : Number(baseDateIso.slice(0, 4));
    const mo = Number(m[2]);
    const da = Number(m[3]);
    return validMonthDay(mo, da) ? `${yr}-${pad2(mo)}-${pad2(da)}` : null;
  }

  // M/D（曜日付き可）。時刻 '14:00' 等はコロン区切りなので一致しない。
  m = t.match(/^(\d{1,2})\/(\d{1,2})(?!\d)/);
  if (m) {
    const yr = Number(baseDateIso.slice(0, 4));
    const mo = Number(m[1]);
    const da = Number(m[2]);
    return validMonthDay(mo, da) ? `${yr}-${pad2(mo)}-${pad2(da)}` : null;
  }

  return null;
}

// S-066-9 / DEC-066 真因(b): parseDateHeader が確定できなかった行が「日付見出しのつもりだが
// 読み取れない」ものか判定する（純関数・検出のみ）。新規パース経路は作らず parseDateHeader と
// 同じ形式語彙を再利用する。
//   返り値が非 null＝曖昧見出し（fail-closed 隔離トリガ）。null＝本文（従来どおり当日に追記）。
//   設計方針: 偽陽性（本文を見出し誤検出）を避けるため、日付固有性の高い形だけを拾う。
//     - 時刻行（HH:MM を含む）は予定本文なので必ず除外する（"14:00 三菱電機" 等）。
//     - 受理済みの正規日付は parseDateHeader が非 null を返すのでこの関数には到達しない。
export function classifyAmbiguousHeader(line: string): AmbiguityReason | null {
  const t = (line || '').trim();
  if (!t) return null;

  // 時刻を含む行は予定本文（見出しではない）。見出し誤検出を防ぐため最優先で除外。
  if (/\d{1,2}\s*:\s*\d{2}/.test(t)) return null;

  // 日付の「形」を持つ行は月/日を実数で検証する（parseDateHeader と同じ妥当域）。
  //   妥当域内＝正規の日付なので parseDateHeader が拾う → ここでは曖昧扱いしない（null）。
  //   妥当域外＝タイポした日付見出し → 'invalid-date'（standalone でも自己整合・呼出順に依存しない）。
  let m = t.match(/^\d{4}[-/](\d{1,2})[-/](\d{1,2})/); // YYYY-MM-DD / YYYY/MM/DD
  if (m) return validMonthDay(Number(m[1]), Number(m[2])) ? null : 'invalid-date';
  m = t.match(/^(?:\d{4}年)?(\d{1,2})月(\d{1,2})日/); // （YYYY年）?M月D日
  if (m) return validMonthDay(Number(m[1]), Number(m[2])) ? null : 'invalid-date';
  m = t.match(/^(\d{1,2})\/(\d{1,2})(?!\d)/); // M/D
  if (m) return validMonthDay(Number(m[1]), Number(m[2])) ? null : 'invalid-date';

  // 全角スラッシュの日付（受理形式に無いため parseDateHeader は拾えない）。
  //   妥当域内なら確認を促す nonstandard-separator、域外なら invalid-date。
  m = t.match(/^(\d{1,2})／(\d{1,2})(?!\d)/);
  if (m) return validMonthDay(Number(m[1]), Number(m[2])) ? 'nonstandard-separator' : 'invalid-date';

  // 月の無い「N日」見出し（例 20日 / 20日（金）。直前の日に後続が混入しやすい）。
  if (/^\d{1,2}\s*日(?!\d)/.test(t)) return 'day-only';

  return null;
}

// 生テキストを日付ヘッダで分割する（純関数）。
//   - 行を走査し、日付ヘッダ行に当たるたびに現在の日付バケットを切替える。
//   - ヘッダ行自体もその日の生テキストに含める（原文保管・コンテキスト保持）。
//   - 同じ日付が複数回出てきたら 1 バケットにマージ（1 日 1 doc）。
//   - 最初のヘッダより前の行は undated バケットへ（RT8「日付なし」で保持）。
//   - S-066-9 / DEC-066 真因(b) fail-closed: 曖昧/欠落した日付見出し（classifyAmbiguousHeader
//     が非 null）を検出したら、以降の本文行を直前の日付バケツに無条件追記せず undated へ隔離する。
//     ＝別日の予定が誤った日付 doc に漏れ込むのを防ぐ（確定見出しが再度現れるまで隔離継続）。
export function splitByDate(raw: string, baseDateIso: string): SplitResult {
  const lines = String(raw || '').split(/\r?\n/);
  const buckets = new Map<string, string[]>();
  const undatedLines: string[] = [];
  const ambiguous: AmbiguousHeader[] = [];
  let current: string | null = null;
  // 曖昧見出し検出後、次の確定見出しが来るまで current への追記を止める（fail-closed 隔離区間）。
  let isolating = false;

  for (const line of lines) {
    const dayId = parseDateHeader(line, baseDateIso);
    if (dayId) {
      current = dayId;
      isolating = false; // 確定見出しで隔離解除。
      if (!buckets.has(dayId)) buckets.set(dayId, []);
      buckets.get(dayId)!.push(line);
      continue;
    }

    const reason = classifyAmbiguousHeader(line);
    if (reason) {
      // 曖昧な日付見出し。直前の日に後続を混ぜないよう隔離を開始し、見出し自身も undated へ。
      ambiguous.push({ reason, line: line.trim() });
      isolating = true;
      undatedLines.push(line);
      continue;
    }

    if (isolating || !current) {
      // 曖昧見出し以降の本文、または最初の確定見出し前の行 → 誤った日付 doc に書かず undated へ。
      undatedLines.push(line);
    } else {
      buckets.get(current)!.push(line);
    }
  }

  const days: RawDay[] = Array.from(buckets.entries())
    .map(([dayId, ls]) => ({ dayId, rawText: ls.join('\n').trim() }))
    .filter((d) => d.rawText.length > 0)
    .sort((a, b) => (a.dayId < b.dayId ? -1 : a.dayId > b.dayId ? 1 : 0));

  return { days, undated: undatedLines.join('\n').trim(), ambiguous };
}

// PII マスキング（RT6・保存前必須）。メールアドレス・電話番号を検出して置換する。
//   過剰マスクより漏れ防止を優先（生テキスト原文保管のため、保存前に確実に落とす）。
//   会社名・担当者名は構造化しないのでマスクしない（議事録生成の相手先推測に必要）。
export function maskPii(text: string): string {
  if (!text) return text;
  let out = String(text);
  // メールアドレス。
  out = out.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[メール]');
  // 電話番号（日本の固定/携帯/フリーダイヤルを広めに・ハイフン/括弧区切り許容）。
  out = out.replace(/0\d{1,3}[-(\s]?\d{2,4}[-)\s]?\d{3,4}/g, '[電話]');
  return out;
}
